import React, { useState, useMemo } from 'react';
import { MovaticFieldGroupAsyncSelect } from '../Redesing/movatic-field-group';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { useCouponsRelayQuery, type CouponGraphQL } from '../../hooks/coupons/query/V2';

export interface SelectedCoupon {
  /** Coupon legacy `oldId` (used as `couponOldId` when granting). */
  oldId: string;
  /** Coupon display name. */
  name: string;
  /** Coupon code, if any. */
  code: string | null;
  /** Display label. */
  label: string;
  /** The full coupon record. */
  original: CouponGraphQL;
}

interface CouponOption {
  value: string;
  label: string;
  original: CouponGraphQL;
}

function couponLabel(coupon: CouponGraphQL): string {
  const parts = [coupon.name];
  if (coupon.code) parts.push(`(${coupon.code})`);
  if (coupon.assignmentOnly) parts.push('· assignment only');
  return parts.filter(Boolean).join(' ');
}

/**
 * Async-select for finding a coupon applicable to a system, backed by the
 * existing `listCoupons` Relay query (scoped via `applicableToSystem`, which
 * includes the system's own coupons plus those inherited from its org).
 *
 * All applicable coupons are included — even `assignmentOnly` ones — since a
 * CS admin granting an assignment-only coupon to a user is the whole point of
 * this picker. Emits the selected coupon to the parent.
 */
function CouponPicker({
  applicableToSystem,
  description,
  selectedCoupon,
  onChange,
}: {
  /** System legacy `oldId` whose applicable coupons should be listed. */
  applicableToSystem?: string;
  description?: string;
  selectedCoupon?: { value: string; label: string } | null;
  onChange: (coupon: SelectedCoupon | null) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const debouncedSearch = useSearchDebounce(search, 500);

  // Scope to coupons applicable to the system and filter by a name term when
  // the admin types one. `name` is passed through the standard coupon filter.
  const filter = useMemo(() => {
    const base: { applicableToSystem?: string; name?: string } = {};
    if (applicableToSystem) base.applicableToSystem = applicableToSystem;
    const term = debouncedSearch.trim();
    if (term) {
      base.name = `%${term}%`;
    }
    return base;
  }, [applicableToSystem, debouncedSearch]);

  const { data, isLoading, isRefetching } = useCouponsRelayQuery(filter, {
    limit: 25,
    enabled: Boolean(applicableToSystem),
  });

  const options: CouponOption[] = useMemo(
    () =>
      (data ?? [])
        // Only active coupons are grantable; inactive ones are filtered out.
        .filter((coupon) => coupon.active !== false)
        .map((coupon) => ({
          value: coupon.oldId ?? coupon.id,
          label: couponLabel(coupon),
          original: coupon,
        })),
    [data]
  );

  return (
    <MovaticFieldGroupAsyncSelect
      isLoading={isLoading || isRefetching}
      label="Coupon"
      description={description}
      name="coupon"
      id="user-grant-coupon"
      dataId="user-grant-coupon-select"
      placeholder="Search by coupon name"
      showClearButton
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={options}
      value={selectedCoupon ?? null}
      onChange={(_event: React.SyntheticEvent, newValue: CouponOption | null) => {
        if (newValue) {
          onChange({
            oldId: newValue.original.oldId ?? newValue.original.id,
            name: newValue.original.name,
            code: newValue.original.code ?? null,
            label: newValue.label,
            original: newValue.original,
          });
        } else {
          onChange(null);
        }
        setIsOpen(false);
      }}
      getOptionText={(option: any) => {
        if (typeof option === 'string') return option;
        return option?.label ?? '';
      }}
      handleSearch={(e) => setSearch(e.target.value)}
      isOptionEqualToValue={(option: any, value: any) => {
        if (!option || !value) return false;
        return option.value === value.value;
      }}
    />
  );
}

export default CouponPicker;
